/**
 * textarea.tsx
 * ─────────────────────────────────────────────────────────────
 * Cognisun CRM — Standard Textarea Component
 *
 * DESIGN CONTRACT (matches Input exactly except for height):
 *   Min-height: 100px
 *   Radius:     8px (rounded-lg)
 *   Border:     1px solid #E2E8F0
 *   Background: #FFFFFF
 *   Text:       13px, #0F172A
 *   Padding:    px-3 py-2.5
 *   Shadow:     0 1px 2px rgba(0,0,0,0.06) — uniform subtle elevation
 *   Resize:     vertical
 *
 * STATES:
 *   hover    → border #CBD5E1  (shadow unchanged)
 *   focus    → border #008ED3  + base shadow + thin primary glow ring
 *   disabled → bg #F8FAFC, text #9CA3AF, opacity-60
 */

import * as React from "react";
import { cn } from "./utils";

function Textarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        // ── Layout ──
        "flex min-h-[100px] w-full",
        // ── Shape — 8px, same as Input ──
        "rounded-lg",
        // ── Border ──
        "border border-[#E2E8F0]",
        // ── Background & text ──
        "bg-white",
        "text-[13px] font-normal text-[#0F172A]",
        "placeholder:text-[#9CA3AF]",
        // ── Padding ──
        "px-3 py-2.5",
        // ── Resize ──
        "resize-y",
        // ── Subtle elevation — same as Input/Select ──
        "shadow-[0_1px_2px_rgba(0,0,0,0.06)]",
        // ── Interaction ──
        "cursor-text outline-none",
        // ── Transitions — shadow excluded so it never grows on hover ──
        "transition-[border-color,background-color] duration-150",
        // ── Hover: minimal border shift — shadow unchanged ──
        "hover:border-[#CBD5E1]",
        // ── Focus: primary blue border + base shadow + thin glow ring ──
        "focus:border-[#008ED3]",
        "focus:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_2px_rgba(0,142,211,0.14)]",
        "focus:ring-0",
        "focus:bg-white",
        // ── Disabled ──
        "disabled:bg-[#F8FAFC] disabled:text-[#9CA3AF]",
        "disabled:border-[#E2E8F0] disabled:cursor-not-allowed disabled:opacity-60",
        // ── Error ──
        "aria-invalid:border-[#EF4444] aria-invalid:shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_2px_rgba(239,68,68,0.14)]",
        // ── Selection ──
        "selection:bg-[#008ED3]/20 selection:text-[#0F172A]",
        className,
      )}
      {...props}
    />
  );
}

export { Textarea };